"use client"

import { ArrowRight } from "lucide-react"
import { AppLink } from "@/components/navigation/app-link"
import { cn } from "@/lib/utils"
import { resolveRosterMediaUrl, rosterPlayerInitials } from "@/lib/roster-media"
import type { RosterPlayerSummary } from "@/types/roster-types"

type Props = {
  player: RosterPlayerSummary
  className?: string
  /** Show a “View profile” affordance under the name (players grid). */
  showCta?: boolean
  /** Prefer immediate load for above-the-fold / marquee cards. */
  priority?: boolean
}

export function RosterPlayerCard({
  player,
  className,
  showCta = false,
  priority = false,
}: Props) {
  const image = resolveRosterMediaUrl(player.mainPhotoUrl)
  const primaryPosition =
    player.positions?.[0] || player.position || null
  const subtitle = [primaryPosition, player.currentClub].filter(Boolean).join(" · ")

  return (
    <AppLink
      href={`/players/${player.slug}`}
      className={cn(
        "group flex h-full min-h-0 flex-col self-stretch outline-none transition-transform duration-300 hover:-translate-y-0.5 focus-visible:ring-2 focus-visible:ring-blue-500/40",
        className
      )}
    >
      <article className="flex h-full min-h-0 flex-col">
        <div className="flex h-full min-h-0 flex-col rounded-2xl bg-slate-200/60 p-px">
          <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden rounded-2xl bg-gradient-to-b from-white to-slate-50 shadow-sm">
            <div className="relative aspect-[3/4] w-full overflow-hidden bg-gradient-to-br from-slate-100 via-slate-50 to-blue-50">
              {image ? (
                <img
                  src={image}
                  alt={player.name}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                  loading={priority ? "eager" : "lazy"}
                  fetchPriority={priority ? "high" : "auto"}
                  decoding="async"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center">
                  <span className="text-3xl font-semibold tracking-tight text-slate-400">
                    {rosterPlayerInitials(player.name)}
                  </span>
                </div>
              )}
              <div
                className="pointer-events-none absolute inset-0 bg-gradient-to-t from-slate-900/15 to-transparent opacity-50"
                aria-hidden
              />
            </div>
            <div
              className={cn(
                "flex shrink-0 flex-col justify-center px-4 py-3",
                showCta ? "min-h-[88px]" : "min-h-[72px]"
              )}
            >
              <h3 className="truncate font-semibold leading-tight tracking-tight text-slate-900">
                {player.name}
              </h3>
              {subtitle ? (
                <p className="mt-1 truncate text-sm font-medium leading-relaxed text-slate-500">
                  {subtitle}
                </p>
              ) : null}
              {showCta ? (
                <span className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-blue-600 transition group-hover:gap-1.5">
                  View profile
                  <ArrowRight className="h-3.5 w-3.5" />
                </span>
              ) : null}
            </div>
          </div>
        </div>
      </article>
    </AppLink>
  )
}
